import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { SITE } from '@/data/site'
import { PRIMARY_NAV } from '@/data/navigation'

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [buyMenuOpen, setBuyMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
    setBuyMenuOpen(false)
  }, [location.pathname])

  const buyNav = PRIMARY_NAV.find((n) => n.label === 'What We Buy')
  const otherNav = PRIMARY_NAV.filter((n) => n.label !== 'What We Buy')

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled ? 'bg-primary-600 shadow-lg' : 'bg-primary-600/95 backdrop-blur-sm',
      )}
      role="banner"
    >
      <div className="container-content">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex flex-col leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-accent rounded"
            aria-label={`${SITE.name} — Home`}
          >
            <span className="font-heading text-lg md:text-xl font-bold text-white">
              W.B. Coin & Jewelry
            </span>
            <span className="text-xs text-gold hidden sm:block">
              Gold · Coins · Jewelry · Watches
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary navigation">
            {/* What We Buy dropdown */}
            <div className="relative">
              <button
                className={cn('nav-link px-3 py-2 rounded flex items-center gap-1')}
                aria-expanded={buyMenuOpen}
                aria-haspopup="true"
                onMouseEnter={() => setBuyMenuOpen(true)}
                onMouseLeave={() => setBuyMenuOpen(false)}
                onClick={() => setBuyMenuOpen((v) => !v)}
              >
                What We Buy
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {buyMenuOpen && (
                <div
                  className="absolute top-full left-0 w-52 bg-white rounded-xl shadow-xl border border-cream-200 py-2 mt-1"
                  onMouseEnter={() => setBuyMenuOpen(true)}
                  onMouseLeave={() => setBuyMenuOpen(false)}
                >
                  {buyNav?.children?.map((child) => (
                    <NavLink
                      key={child.href}
                      to={child.href}
                      className={({ isActive }) =>
                        cn(
                          'block px-4 py-2.5 text-sm font-medium text-neutral-dark hover:bg-cream-100 hover:text-gold-600 transition-colors',
                          isActive && 'text-gold-600 bg-cream-100',
                        )
                      }
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {otherNav.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn('nav-link px-3 py-2 rounded', isActive && 'nav-link-active')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <a
            href={SITE.phoneHref}
            className="hidden md:inline-flex btn-primary btn btn-sm"
            aria-label={`Call us at ${SITE.phone}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {SITE.phone}
          </a>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 text-white hover:text-gold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-accent rounded"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-primary-700 border-t border-primary-600" role="navigation" aria-label="Mobile navigation">
          <div className="container-content py-4 space-y-1">
            {PRIMARY_NAV.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <p className="px-3 py-2 text-xs font-semibold uppercase tracking-widest text-gold/70">
                    {item.label}
                  </p>
                  {item.children.map((child) => (
                    <NavLink
                      key={child.href}
                      to={child.href}
                      className={({ isActive }) =>
                        cn(
                          'block px-5 py-2 text-sm text-white/80 hover:text-white hover:bg-primary-600 rounded transition-colors',
                          isActive && 'text-gold',
                        )
                      }
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'block px-3 py-2.5 text-sm font-medium text-white/80 hover:text-white hover:bg-primary-600 rounded transition-colors',
                      isActive && 'text-gold',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
            <div className="pt-2 border-t border-primary-600">
              <a
                href={SITE.phoneHref}
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-gold hover:text-gold-accent"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
